import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CopyToClipboard,
  Header,
  Icon,
  Link,
  Modal,
  Popover,
  PropertyFilter,
  PropertyFilterProps,
  SpaceBetween,
  Spinner,
  StatusIndicator,
  Table,
  TableProps,
  Tabs,
} from "@amzn/awsui-components-react";
import { CodeView } from "@amzn/awsui-code-view";
import { useCollection } from "@amzn/awsui-collection-hooks";
import {
  ClassificationEntry,
  FinancialAmountRow,
  ShadowTestDiscrepancy,
  ShadowTestLine,
  ShadowTestMatchSet,
  Stage,
} from "../../lusca-search-api/generated-src";
import { COUNTRY_TO_MARKETPLACE } from "../../helpers/marketplaceUtils";
import { downloadShadowTestCSV } from "../../helpers/csvExportUtils";
import { createFlashUIMatcherLink } from "../../helpers/linkUtils";
import {
  groupShadowTestItemsByGti,
  ShadowTestGtiGroup,
} from "../../helpers/shadowTestBundleUtils";
import {
  ShadowTestPivotByProduct,
  ShadowTestResultResponseView,
} from "../../types/shadowTestResult";

interface BundleTableRow<T> {
  id: string;
  kind: "group" | "item";
  item?: T;
  group?: ShadowTestGtiGroup<T>;
  children: BundleTableRow<T>[];
}

// Wraps a table so it scrolls horizontally instead of wrapping cell text. Combined with
// per-column minWidth (which forces the table wider than the modal), this keeps every row on
// a single line and lets the user scroll sideways to see the remaining columns.
const HorizontalScroll = ({ children }: { children: React.ReactNode }) => (
  <div style={{ overflowX: "auto", width: "100%" }}>{children}</div>
);

// Ensures each unique line carries an environment so the merged Unique tab stays sortable and
// filterable by source. Leaves an already-set environment untouched; fills the given default
// otherwise. Returns [] for a missing/empty source array.
const withEnvironmentFallback = (
  lines: ShadowTestLine[] | undefined,
  defaultEnvironment: string
): ShadowTestLine[] =>
  (lines ?? []).map((line) =>
    line.environment ? line : { ...line, environment: defaultEnvironment }
  );

const createBundleRows = <T,>(
  groups: ShadowTestGtiGroup<T>[]
): BundleTableRow<T>[] =>
  groups.map((group, groupIndex) => {
    const groupId = `group-${group.gti || "unassigned"}-${groupIndex}`;
    return {
      id: groupId,
      kind: "group",
      group,
      children: group.items.map((item, itemIndex) => ({
        id: `${groupId}-item-${itemIndex}`,
        kind: "item",
        item,
        children: [],
      })),
    };
  });

// Build a comparator for the underlying items from a column's sorting metadata. Mirrors how
// Cloudscape/collection-hooks sorts: prefer an explicit comparator, otherwise compare the
// sortingField values as locale-aware strings. Returns null when the column is not sortable.
const columnItemComparator = <T,>(
  column: TableProps.ColumnDefinition<T>
): ((a: T, b: T) => number) | null => {
  if (column.sortingComparator) {
    return column.sortingComparator;
  }
  if (column.sortingField) {
    const field = column.sortingField as keyof T;
    return (a, b) =>
      String(a[field] ?? "").localeCompare(String(b[field] ?? ""));
  }
  return null;
};

// PropertyFilter i18n. Shared across every filterable table in the modal.
const FILTER_I18N_STRINGS: PropertyFilterProps.I18nStrings = {
  filteringAriaLabel: "Filter rows",
  filteringPlaceholder: "Filter rows by column",
  operationAndText: "and",
  operationOrText: "or",
  operatorsText: "Operators",
  operatorContainsText: "Contains",
  operatorDoesNotContainText: "Does not contain",
  operatorEqualsText: "Equals",
  operatorDoesNotEqualText: "Does not equal",
  propertyText: "Property",
  operatorText: "Operator",
  valueText: "Value",
  cancelActionText: "Cancel",
  applyActionText: "Apply",
  clearFiltersText: "Clear filters",
  groupValuesText: "Values",
  groupPropertiesText: "Properties",
  tokenLimitShowMore: "Show more",
  tokenLimitShowFewer: "Show fewer",
  editTokenHeader: "Edit filter",
  removeTokenButtonAriaLabel: (token) =>
    `Remove filter ${token.propertyLabel} ${token.operator} ${token.value}`,
  dismissAriaLabel: "Dismiss",
  enteredTextLabel: (text: string) => `Use: "${text}"`,
};

// Every column that is sortable is also filterable. String columns filter with contains/equals;
// the two amount columns are stored as strings too, so they use the same string operators.
const createFilteringProperties = <T,>(
  columnDefinitions: TableProps.ColumnDefinition<T>[]
): PropertyFilterProps.FilteringProperty[] =>
  columnDefinitions
    .filter((column) => column.sortingField)
    .map((column) => ({
      key: column.sortingField as string,
      propertyLabel:
        typeof column.header === "string" ? column.header : String(column.id),
      groupValuesLabel: `${
        typeof column.header === "string" ? column.header : String(column.id)
      } values`,
      operators: [":", "!:", "=", "!="],
    }));

const createBundleColumns = <T,>(
  columnDefinitions: TableProps.ColumnDefinition<T>[]
): TableProps.ColumnDefinition<BundleTableRow<T>>[] =>
  columnDefinitions.map((column, index) => {
    // Keep the header clickable/sortable in bundle mode. The actual ordering is applied within
    // each GTI group by BundleSortableTable (controlled sorting), so this comparator only needs
    // to exist and be well-defined; group header rows sort equal to each other.
    const itemComparator = columnItemComparator(column);
    return {
      id: column.id,
      header: column.header,
      width: column.width,
      minWidth: column.minWidth,
      maxWidth: column.maxWidth,
      isRowHeader: column.isRowHeader,
      verticalAlign: column.verticalAlign,
      hasDynamicContent: column.hasDynamicContent,
      sortingComparator: itemComparator
        ? (rowA: BundleTableRow<T>, rowB: BundleTableRow<T>) =>
            rowA.item && rowB.item ? itemComparator(rowA.item, rowB.item) : 0
        : undefined,
      cell: (row) => {
        if (row.kind === "item" && row.item) {
          return column.cell(row.item);
        }
        if (index > 0 || !row.group) {
          return null;
        }

        const { group } = row;
        const label = group.gti ? `GTI ${group.gti}` : "No GTI";
        const lineLabel = `${group.items.length} ${
          group.items.length === 1 ? "line" : "lines"
        }`;
        const usecaseLabel = `${group.usecases.length} ${
          group.usecases.length === 1 ? "usecase" : "usecases"
        }`;

        return (
          <span
            style={{
              display: "inline-block",
              whiteSpace: "nowrap",
            }}
          >
            {group.passed !== undefined && (
              <>
                <StatusIndicator type={group.passed ? "success" : "error"}>
                  {group.passed ? "PASS" : "FAIL"}
                </StatusIndicator>
                <Box variant="span" color="text-body-secondary">
                  {" "}
                  ·{" "}
                </Box>
              </>
            )}
            <Box variant="strong" display="inline">
              {label}
            </Box>
            <Box variant="span" color="text-body-secondary">
              {" "}
              · {lineLabel} · {usecaseLabel}
            </Box>
          </span>
        );
      },
    };
  });

// The subset of useCollection's propertyFilterProps that PropertyFilter consumes.
type CollectionPropertyFilterProps = {
  query: PropertyFilterProps.Query;
  onChange: PropertyFilterProps["onChange"];
  filteringProperties: readonly PropertyFilterProps.FilteringProperty[];
};

// Column-aware property filter shown above each table. Kept outside HorizontalScroll so its
// dropdown is not clipped by the scroll container. Renders nothing when no column is filterable.
function ColumnFilter({
  propertyFilterProps,
  filteredItemsCount,
  totalCount,
}: {
  propertyFilterProps: CollectionPropertyFilterProps;
  filteredItemsCount: number;
  totalCount: number;
}) {
  if (propertyFilterProps.filteringProperties.length === 0) {
    return null;
  }
  const countText =
    filteredItemsCount === totalCount
      ? `${totalCount} rows`
      : `${filteredItemsCount} of ${totalCount} rows`;
  return (
    <PropertyFilter
      {...propertyFilterProps}
      i18nStrings={FILTER_I18N_STRINGS}
      countText={
        propertyFilterProps.query.tokens.length > 0 ? countText : undefined
      }
      expandToViewport
    />
  );
}

// Flat, sortable + filterable table. Column headers sort; the property filter narrows rows by any
// column (contains/equals). Both are driven by useCollection.
function PlainSortableTable<T>({
  items,
  columnDefinitions,
}: {
  items: T[];
  columnDefinitions: TableProps.ColumnDefinition<T>[];
}) {
  const filteringProperties = useMemo(
    () => createFilteringProperties(columnDefinitions),
    [columnDefinitions]
  );
  const {
    items: visibleItems,
    collectionProps,
    propertyFilterProps,
  } = useCollection(items, {
    sorting: {},
    propertyFiltering: { filteringProperties },
  });
  return (
    <SpaceBetween size="xs">
      <ColumnFilter
        propertyFilterProps={propertyFilterProps}
        filteredItemsCount={visibleItems.length}
        totalCount={items.length}
      />
      <HorizontalScroll>
        <Table<T>
          {...collectionProps}
          items={visibleItems}
          columnDefinitions={columnDefinitions}
          variant="embedded"
        />
      </HorizontalScroll>
    </SpaceBetween>
  );
}

// Grouped (bundle) table. Filtering runs on the flat lines first (reusing useCollection's matcher),
// then the surviving lines are re-grouped by GTI so empty groups disappear. GTI groups keep their
// natural order; clicking a column header sorts the lines within each group. Cloudscape only sorts
// the top-level rows, so we sort each group's children ourselves and drive the header state.
function BundleSortableTable<
  T extends { gti?: string; passed?: boolean; usecase?: string }
>({
  items,
  columnDefinitions,
}: {
  items: T[];
  columnDefinitions: TableProps.ColumnDefinition<T>[];
}) {
  const [expandedItems, setExpandedItems] = useState<BundleTableRow<T>[]>([]);
  const [sortingColumn, setSortingColumn] =
    useState<TableProps.SortingColumn<BundleTableRow<T>>>();
  const [isDescending, setIsDescending] = useState(false);

  const filteringProperties = useMemo(
    () => createFilteringProperties(columnDefinitions),
    [columnDefinitions]
  );
  const { items: visibleItems, propertyFilterProps } = useCollection(items, {
    propertyFiltering: { filteringProperties },
  });

  const groupedColumns = useMemo(
    () => createBundleColumns(columnDefinitions),
    [columnDefinitions]
  );

  const groupedRows = useMemo(() => {
    const rows = createBundleRows(groupShadowTestItemsByGti([...visibleItems]));
    if (!sortingColumn?.sortingComparator) {
      return rows;
    }
    const comparator = sortingColumn.sortingComparator;
    const direction = isDescending ? -1 : 1;
    return rows.map((row) => ({
      ...row,
      children: [...row.children].sort((a, b) => direction * comparator(a, b)),
    }));
  }, [visibleItems, sortingColumn, isDescending]);

  return (
    <SpaceBetween size="xs">
      <ColumnFilter
        propertyFilterProps={propertyFilterProps}
        filteredItemsCount={visibleItems.length}
        totalCount={items.length}
      />
      <HorizontalScroll>
        <Table<BundleTableRow<T>>
          items={groupedRows}
          trackBy="id"
          columnDefinitions={groupedColumns}
          sortingColumn={sortingColumn}
          sortingDescending={isDescending}
          onSortingChange={({ detail }) => {
            setSortingColumn(detail.sortingColumn);
            setIsDescending(detail.isDescending ?? false);
          }}
          expandableRows={{
            getItemChildren: (row) => row.children,
            isItemExpandable: (row) => row.kind === "group",
            expandedItems,
            onExpandableItemToggle: ({ detail }) =>
              setExpandedItems((currentItems) =>
                detail.expanded
                  ? [...currentItems, detail.item]
                  : currentItems.filter((item) => item.id !== detail.item.id)
              ),
            getItemsCount: (row) => row.children.length,
          }}
          ariaLabels={{
            expandButtonLabel: (row) => `Expand ${row.group?.gti || "No GTI"}`,
            collapseButtonLabel: (row) =>
              `Collapse ${row.group?.gti || "No GTI"}`,
          }}
          variant="embedded"
        />
      </HorizontalScroll>
    </SpaceBetween>
  );
}

function BundleAwareTable<
  T extends { gti?: string; passed?: boolean; usecase?: string }
>({
  isBundle,
  items,
  columnDefinitions,
}: {
  isBundle?: boolean;
  items: T[];
  columnDefinitions: TableProps.ColumnDefinition<T>[];
}) {
  if (!isBundle) {
    return (
      <PlainSortableTable items={items} columnDefinitions={columnDefinitions} />
    );
  }

  return (
    <BundleSortableTable items={items} columnDefinitions={columnDefinitions} />
  );
}

interface ShadowTestResultModalProps {
  visible: boolean;
  onDismiss: () => void;
  result: ShadowTestResultResponseView | null | undefined;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  onRefresh: () => void;
  cliCommand: string;
  classifications: Record<string, ClassificationEntry>;
  onTriggerTest: () => void;
  isTriggerLoading: boolean;
  triggerError: Error | null;
  canTrigger: boolean;
}

export default function ShadowTestResultModal({
  visible,
  onDismiss,
  result,
  isLoading,
  isRefreshing,
  error,
  onRefresh,
  cliCommand,
  classifications,
  onTriggerTest,
  isTriggerLoading,
  triggerError,
  canTrigger,
}: ShadowTestResultModalProps) {
  const [triggerSuccess, setTriggerSuccess] = useState(false);
  const [triggerFailed, setTriggerFailed] = useState(false);
  const wasTriggerLoadingRef = useRef(false);

  useEffect(() => {
    if (!visible) {
      setTriggerSuccess(false);
      setTriggerFailed(false);
      wasTriggerLoadingRef.current = false;
    }
  }, [visible]);

  useEffect(() => {
    setTriggerSuccess(false);
    setTriggerFailed(false);
  }, [result]);

  useEffect(() => {
    if (isTriggerLoading) {
      wasTriggerLoadingRef.current = true;
      setTriggerSuccess(false);
      setTriggerFailed(false);
    } else if (wasTriggerLoadingRef.current) {
      wasTriggerLoadingRef.current = false;
      if (triggerError) {
        setTriggerFailed(true);
      } else {
        setTriggerSuccess(true);
      }
    }
  }, [isTriggerLoading, triggerError]);

  const renderContent = () => {
    if (isLoading) {
      return (
        <Box textAlign="center" padding="l">
          <Spinner size="large" />
        </Box>
      );
    }

    if (error) {
      return (
        <SpaceBetween size="l">
          <Box textAlign="center" padding="l">
            <StatusIndicator type="error">
              {error.message || "An unexpected error occurred."}
            </StatusIndicator>
          </Box>
          <Box textAlign="center">
            <Button iconName="refresh" onClick={onRefresh}>
              Retry
            </Button>
          </Box>
          <CliCommandSection cliCommand={cliCommand} />
        </SpaceBetween>
      );
    }

    if (!result) {
      return (
        <SpaceBetween size="l">
          <Box textAlign="center" padding="l" color="text-status-inactive">
            No shadow test results found for this transaction.
          </Box>
          <Box textAlign="center">
            <SpaceBetween size="xs">
              <Button
                variant="primary"
                onClick={onTriggerTest}
                loading={isTriggerLoading}
                disabled={!canTrigger}
              >
                Run Shadow Test
              </Button>
              {triggerSuccess && (
                <StatusIndicator type="success">Test triggered</StatusIndicator>
              )}
              {triggerFailed && (
                <StatusIndicator type="error">Test failed</StatusIndicator>
              )}
            </SpaceBetween>
          </Box>
          <CliCommandSection cliCommand={cliCommand} />
        </SpaceBetween>
      );
    }

    const testDate = result.testTimestamp
      ? new Date(result.testTimestamp).toLocaleString()
      : "";

    const tabs: { id: string; label: string; content: JSX.Element }[] = [];

    if (result.matchedLines && result.matchedLines.length > 0) {
      tabs.push({
        id: "matched",
        label: `Matched (${result.matchedLines.length})`,
        content: (
          <LineTable
            isBundle={result.isBundle}
            lines={result.matchedLines}
            classifications={classifications}
          />
        ),
      });
    }

    // All unique lines share one tab. The Environment column distinguishes gamma / prod / jem, and
    // the new per-column filter lets accountants narrow to a single environment when needed. Fall
    // back to the source array's environment when the backend left the field unset.
    const uniqueLines = [
      ...withEnvironmentFallback(result.gammaUniqueLines, "gamma"),
      ...withEnvironmentFallback(result.prodUniqueLines, "prod"),
      ...withEnvironmentFallback(result.jemUniqueLines, "jem"),
    ];

    if (uniqueLines.length > 0) {
      tabs.push({
        id: "unique",
        label: `Unique (${uniqueLines.length})`,
        content: (
          <LineTable
            isBundle={result.isBundle}
            lines={uniqueLines}
            classifications={classifications}
          />
        ),
      });
    }

    if (result.discrepancies && result.discrepancies.length > 0) {
      tabs.push({
        id: "discrepancies",
        label: `Discrepancies (${result.discrepancies.length})`,
        content: (
          <DiscrepancyTable
            isBundle={result.isBundle}
            discrepancies={result.discrepancies}
            classifications={classifications}
          />
        ),
      });
    }

    if (result.matchSets && result.matchSets.length > 0) {
      tabs.push({
        id: "match-sets",
        label: `Match Sets (${result.matchSets.length})`,
        content: (
          <MatchSetTable
            matchSets={result.matchSets}
            countryCode={result.countryCode}
            classifications={classifications}
          />
        ),
      });
    }

    if (
      result.gammaPivot ||
      result.prodPivot ||
      result.financialAmounts ||
      result.bundleAllocation
    ) {
      tabs.push({
        id: "account-balance",
        label: "Financial Validation",
        content: (
          <SpaceBetween size="l">
            {result.financialAmounts &&
              Object.keys(result.financialAmounts).length > 0 && (
                <SpaceBetween size="xs">
                  <Box variant="h4">Financial Amounts</Box>
                  <FinancialAmountsTable rows={result.financialAmounts} />
                </SpaceBetween>
              )}
            {result.bundleAllocation &&
              Object.keys(result.bundleAllocation).length > 0 && (
                <SpaceBetween size="xs">
                  <BundleAllocationTables
                    rows={result.bundleAllocation}
                    paymentIntegrityPassed={
                      result.bundleAllocationPaymentIntegrityPassed
                    }
                  />
                </SpaceBetween>
              )}
            {result.gammaPivot && Object.keys(result.gammaPivot).length > 0 && (
              <SpaceBetween size="xs">
                <Box variant="h4">Account Balance Pivot - Gamma</Box>
                <PivotTable
                  pivot={result.gammaPivot}
                  pivotByProduct={result.gammaPivotByProduct}
                />
              </SpaceBetween>
            )}
            {result.prodPivot && Object.keys(result.prodPivot).length > 0 && (
              <SpaceBetween size="xs">
                <Box variant="h4">Account Balance Pivot - Prod</Box>
                <PivotTable
                  pivot={result.prodPivot}
                  pivotByProduct={result.prodPivotByProduct}
                />
              </SpaceBetween>
            )}
          </SpaceBetween>
        ),
      });
    }

    const usecaseComparison: {
      label: string;
      gamma: string;
      prod: string;
      jem: string;
    }[] = [
      {
        label: "Usecases",
        gamma: result.gammaUsecases?.join(", ") || "-",
        prod: result.prodUsecases?.join(", ") || "-",
        jem: result.jemUsecases?.join(", ") || "-",
      },
      {
        label: "Lines",
        gamma: String(result.totalGammaLines ?? 0),
        prod: String(result.totalProdLines ?? 0),
        jem: "-",
      },
    ];

    return (
      <SpaceBetween size="s">
        <SpaceBetween size="xs" direction="horizontal">
          <StatusIndicator type={result.passed ? "success" : "error"}>
            {result.passed ? "PASS" : "FAIL"}
          </StatusIndicator>
          {result.sourceAmountCheckPassed != null && (
            <>
              <Box color="text-body-secondary">·</Box>
              <StatusIndicator
                type={result.sourceAmountCheckPassed ? "success" : "error"}
              >
                {result.sourceAmountCheckPassed
                  ? "Amounts Match"
                  : "Amount Mismatch"}
              </StatusIndicator>
            </>
          )}
          <Box color="text-body-secondary">·</Box>
          <Box>GL {result.glProductGroup}</Box>
          <Box color="text-body-secondary">·</Box>
          <Box>{result.countryCode}</Box>
          <Box color="text-body-secondary">·</Box>
          <Box>{result.paymentProvider || "-"}</Box>
          {result.isPromo && (
            <>
              <Box color="text-body-secondary">·</Box>
              <Box color="text-status-warning">Promo</Box>
            </>
          )}
          {result.isBundle && (
            <>
              <Box color="text-body-secondary">·</Box>
              <Box>Bundle</Box>
            </>
          )}
          <Box color="text-body-secondary">·</Box>
          <Box color="text-body-secondary">Tested {testDate}</Box>
        </SpaceBetween>

        <Table
          items={usecaseComparison}
          columnDefinitions={[
            {
              id: "label",
              header: "",
              cell: (e) => <Box fontWeight="bold">{e.label}</Box>,
              width: 100,
            },
            { id: "gamma", header: "Gamma", cell: (e) => e.gamma },
            { id: "prod", header: "Prod", cell: (e) => e.prod },
            ...(result.jemUsecases && result.jemUsecases.length > 0
              ? [
                  {
                    id: "jem",
                    header: "JEM",
                    cell: (e: (typeof usecaseComparison)[0]) => e.jem,
                  },
                ]
              : []),
          ]}
          variant="embedded"
          wrapLines
        />

        {tabs.length > 0 && <Tabs tabs={tabs} />}

        <CliCommandSection cliCommand={cliCommand} />
      </SpaceBetween>
    );
  };

  return (
    <Modal
      visible={visible}
      onDismiss={onDismiss}
      size="max"
      header={
        <Header
          actions={
            <SpaceBetween size="xs" direction="horizontal">
              <Button
                iconName="refresh"
                onClick={onRefresh}
                loading={isRefreshing}
              >
                Refresh
              </Button>
              {result && (
                <>
                  <Button
                    iconName="download"
                    onClick={() =>
                      downloadShadowTestCSV(result, classifications)
                    }
                  >
                    Export CSV
                  </Button>
                  <Button
                    onClick={onTriggerTest}
                    loading={isTriggerLoading}
                    disabled={!canTrigger}
                  >
                    Re-run Test
                  </Button>
                  {triggerSuccess && (
                    <StatusIndicator type="success">
                      Test triggered
                    </StatusIndicator>
                  )}
                  {triggerFailed && (
                    <StatusIndicator type="error">Test failed</StatusIndicator>
                  )}
                </>
              )}
            </SpaceBetween>
          }
        >
          Shadow Test Result
        </Header>
      }
    >
      {renderContent()}
    </Modal>
  );
}

const ClassificationCell = ({
  classification,
  passed,
  classifications,
}: {
  classification?: string;
  passed?: boolean;
  classifications: Record<string, ClassificationEntry>;
}) => {
  // StatusIndicator renders custom node content, so Cloudscape's wrapLines does not keep it on a
  // single line. Force nowrap here so the classification text scrolls with the table instead of
  // wrapping inside the cell.
  const indicator = (
    <span style={{ whiteSpace: "nowrap" }}>
      <StatusIndicator type={passed ? "success" : "error"}>
        {classification || "-"}
      </StatusIndicator>
    </span>
  );

  const desc = classification
    ? classifications[classification]?.description
    : undefined;
  if (!desc) return indicator;

  return (
    <Popover
      dismissButton={false}
      position="top"
      size="medium"
      triggerType="custom"
      content={<Box>{desc}</Box>}
    >
      <span
        style={{
          cursor: "help",
          borderBottom: "1px dotted",
          whiteSpace: "nowrap",
        }}
      >
        {indicator}
      </span>
    </Popover>
  );
};

// Comparator for columns whose cell renders a numeric-but-string amount (e.g. "12.34"). Empty or
// non-numeric values sort as the smallest, so blanks group together at one end.
const numericFieldComparator =
  <T,>(getValue: (item: T) => string | undefined) =>
  (a: T, b: T): number => {
    const parse = (v: string | undefined) => {
      const n = Number.parseFloat(v ?? "");
      return Number.isNaN(n) ? Number.NEGATIVE_INFINITY : n;
    };
    return parse(getValue(a)) - parse(getValue(b));
  };

const lineColumns = (
  classifications: Record<string, ClassificationEntry>
): TableProps.ColumnDefinition<ShadowTestLine>[] => [
  {
    id: "usecase",
    header: "Usecase",
    cell: (e: ShadowTestLine) => e.usecase || "-",
    sortingField: "usecase",
    minWidth: 160,
  },
  {
    id: "account",
    header: "Account Number",
    cell: (e: ShadowTestLine) => e.accountNumber || "-",
    sortingField: "accountNumber",
    minWidth: 140,
  },
  {
    id: "side",
    header: "Journal Side",
    cell: (e: ShadowTestLine) => e.journalSide || "-",
    sortingField: "journalSide",
    minWidth: 110,
  },
  {
    id: "companyCode",
    header: "Company Code",
    cell: (e: ShadowTestLine) => e.companyCode || "-",
    sortingField: "companyCode",
    minWidth: 120,
  },
  {
    id: "location",
    header: "Location",
    cell: (e: ShadowTestLine) => e.location || "-",
    sortingField: "location",
    minWidth: 100,
  },
  {
    id: "costCenter",
    header: "Cost Center",
    cell: (e: ShadowTestLine) => e.costCenter || "-",
    sortingField: "costCenter",
    minWidth: 110,
  },
  {
    id: "productLine",
    header: "Product Line",
    cell: (e: ShadowTestLine) => e.productLine || "-",
    sortingField: "productLine",
    minWidth: 110,
  },
  {
    id: "channel",
    header: "Channel",
    cell: (e: ShadowTestLine) => e.channel || "-",
    sortingField: "channel",
    minWidth: 100,
  },
  {
    id: "project",
    header: "Project",
    cell: (e: ShadowTestLine) => e.project || "-",
    sortingField: "project",
    minWidth: 100,
  },
  {
    id: "functionalAmount",
    header: "Functional Amount",
    cell: (e: ShadowTestLine) =>
      e.functionalAmount
        ? `${e.functionalAmount} ${e.functionalCurrency || e.currency || ""}`
        : "-",
    sortingField: "functionalAmount",
    sortingComparator: numericFieldComparator(
      (e: ShadowTestLine) => e.functionalAmount
    ),
    minWidth: 150,
  },
  {
    id: "localAmount",
    header: "Local Amount",
    cell: (e: ShadowTestLine) =>
      e.localAmount
        ? `${e.localAmount} ${e.localCurrency || e.currency || ""}`
        : "-",
    sortingField: "localAmount",
    sortingComparator: numericFieldComparator(
      (e: ShadowTestLine) => e.localAmount
    ),
    minWidth: 140,
  },
  {
    id: "description",
    header: "GL Line Description",
    cell: (e: ShadowTestLine) => e.glLineDescription || "-",
    sortingField: "glLineDescription",
    minWidth: 220,
  },
  {
    id: "setOfBooks",
    header: "Set of Books",
    cell: (e: ShadowTestLine) => e.setOfBooks || "-",
    sortingField: "setOfBooks",
    minWidth: 120,
  },
  {
    id: "matchKey",
    header: "Match Key",
    cell: (e: ShadowTestLine) => e.matchKey || "-",
    sortingField: "matchKey",
    minWidth: 140,
  },
  {
    id: "intent",
    header: "Intent",
    cell: (e: ShadowTestLine) => e.intent || "-",
    sortingField: "intent",
    minWidth: 100,
  },
  {
    id: "env",
    header: "Environment",
    cell: (e: ShadowTestLine) => e.environment || "-",
    sortingField: "environment",
    minWidth: 110,
  },
  {
    id: "classification",
    header: "Classification",
    cell: (e: ShadowTestLine) => (
      <ClassificationCell
        classification={e.classification}
        passed={e.passed}
        classifications={classifications}
      />
    ),
    sortingField: "classification",
    minWidth: 140,
  },
];

// Default order the matched/unique tables load in: usecase first, then GL line description, both
// ascending. Users can re-sort by any column afterwards. In bundle mode this orders the lines
// within each GTI group, since the groups themselves keep their natural order.
const byUsecaseThenDescription = (a: ShadowTestLine, b: ShadowTestLine) =>
  (a.usecase ?? "").localeCompare(b.usecase ?? "") ||
  (a.glLineDescription ?? "").localeCompare(b.glLineDescription ?? "");

const LineTable = ({
  isBundle,
  lines,
  classifications,
}: {
  isBundle?: boolean;
  lines: ShadowTestLine[];
  classifications: Record<string, ClassificationEntry>;
}) => {
  const sortedLines = useMemo(
    () => [...lines].sort(byUsecaseThenDescription),
    [lines]
  );
  return (
    <div style={{ fontSize: "12px" }}>
      <BundleAwareTable
        isBundle={isBundle}
        items={sortedLines}
        columnDefinitions={lineColumns(classifications)}
      />
    </div>
  );
};

const discrepancyColumns = (
  classifications: Record<string, ClassificationEntry>
): TableProps.ColumnDefinition<ShadowTestDiscrepancy>[] => [
  {
    id: "field",
    header: "Field",
    cell: (e) => e.field || "-",
    sortingField: "field",
    minWidth: 140,
  },
  {
    id: "usecase",
    header: "Usecase",
    cell: (e) => e.usecase || "-",
    sortingField: "usecase",
    minWidth: 160,
  },
  {
    id: "gammaValue",
    header: "Gamma",
    cell: (e) => e.gammaValue || "-",
    sortingField: "gammaValue",
    minWidth: 160,
  },
  {
    id: "prodValue",
    header: "Prod",
    cell: (e) => e.prodValue || "-",
    sortingField: "prodValue",
    minWidth: 160,
  },
  {
    id: "jemValue",
    header: "JEM",
    cell: (e) => e.jemValue || "-",
    sortingField: "jemValue",
    minWidth: 160,
  },
  {
    id: "intent",
    header: "Intent",
    cell: (e) => e.intent || "-",
    sortingField: "intent",
    minWidth: 100,
  },
  {
    id: "account",
    header: "Account",
    cell: (e) => e.accountNumber || "-",
    sortingField: "accountNumber",
    minWidth: 140,
  },
  {
    id: "classification",
    header: "Classification",
    cell: (e) => (
      <ClassificationCell
        classification={e.classification}
        passed={e.passed}
        classifications={classifications}
      />
    ),
    sortingField: "classification",
    minWidth: 140,
  },
];

const DiscrepancyTable = ({
  isBundle,
  discrepancies,
  classifications,
}: {
  isBundle?: boolean;
  discrepancies: ShadowTestDiscrepancy[];
  classifications: Record<string, ClassificationEntry>;
}) => (
  <BundleAwareTable
    isBundle={isBundle}
    items={discrepancies}
    columnDefinitions={discrepancyColumns(classifications)}
  />
);

const matchSetColumns = (
  countryCode: string | undefined,
  classifications: Record<string, ClassificationEntry>
): TableProps.ColumnDefinition<ShadowTestMatchSet>[] => [
  {
    id: "env",
    header: "Env",
    cell: (e) => e.environment || "-",
    sortingField: "environment",
    minWidth: 90,
  },
  {
    id: "matchKey",
    header: "Match Key",
    cell: (e) => {
      if (!e.matchKey || !e.accountNumber || !e.environment)
        return e.matchKey || "-";
      const mpId = COUNTRY_TO_MARKETPLACE[countryCode || ""] || "ATVPDKIKX0DER";
      const stage =
        e.environment.toLowerCase() === "prod" ? Stage.Prod : Stage.Gamma;
      const url = createFlashUIMatcherLink(
        mpId,
        e.matchKey,
        stage,
        e.accountNumber
      );
      return (
        <Link href={url} external>
          {e.matchKey}
        </Link>
      );
    },
    sortingField: "matchKey",
    minWidth: 180,
  },
  {
    id: "account",
    header: "Account",
    cell: (e) => e.accountNumber || "-",
    sortingField: "accountNumber",
    minWidth: 140,
  },
  {
    id: "amount",
    header: "Balance",
    cell: (e) => (e.amount ? `${e.amount} ${e.currency || ""}` : "-"),
    sortingField: "amount",
    sortingComparator: numericFieldComparator(
      (e: ShadowTestMatchSet) => e.amount
    ),
    minWidth: 140,
  },
  {
    id: "closed",
    header: "Closed",
    cell: (e) => (
      <StatusIndicator type={e.closed ? "success" : "warning"}>
        {e.closed ? "Yes" : "No"}
      </StatusIndicator>
    ),
    sortingComparator: (a, b) => Number(a.closed ?? 0) - Number(b.closed ?? 0),
    minWidth: 100,
  },
  {
    id: "classification",
    header: "Classification",
    cell: (e) => (
      <ClassificationCell
        classification={e.classification}
        passed={e.passed}
        classifications={classifications}
      />
    ),
    sortingField: "classification",
    minWidth: 140,
  },
];

const MatchSetTable = ({
  matchSets,
  countryCode,
  classifications,
}: {
  matchSets: ShadowTestMatchSet[];
  countryCode?: string;
  classifications: Record<string, ClassificationEntry>;
}) => (
  <PlainSortableTable
    items={matchSets}
    columnDefinitions={matchSetColumns(countryCode, classifications)}
  />
);

const formatLabel = (key: string) =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

// Decide whether a source amount should be flagged as mismatched against a USL amount for the
// Financial Validation tab. When currencies differ we cannot compare on the client (no FX rate
// available here), so suppress row-level red and defer to the backend-computed tab pass/fail.
// When currencies match, compare the string amounts directly (same as before).
const amountMismatch = (
  sourceAmount: string | undefined,
  sourceCurr: string | undefined,
  uslAmount: string | undefined,
  uslCurr: string | undefined
): boolean => {
  if (!sourceAmount || !uslAmount) return false;
  if (sourceCurr && uslCurr && sourceCurr !== uslCurr) return false;
  return sourceAmount !== uslAmount;
};

// Visually hidden but readable by screen readers / assistive tech.
const SR_ONLY: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
  border: 0,
};

const BUNDLE_ALLOCATION_TOTAL_LABEL = "Total Bundle Level";

// Keys must match the cell suffixes the shadow test writes to BUNDLE_ALLOCATION.
const BUNDLE_ALLOCATION_METRICS = [
  { key: "revenue", label: "Revenue" },
  { key: "royalty", label: "Royalty" },
  { key: "tax", label: "Tax" },
  { key: "psaa_cash_receiving", label: "PSAA Cash Receiving" },
  { key: "psaa_stored_value", label: "PSAA Stored Value" },
  { key: "promo", label: "Promo" },
];

interface BundleAllocationRow {
  component: string;
  cells: { [key: string]: string };
  isTotal: boolean;
}

/**
 * Bundle split validation as a single table. Each metric column is split into
 * two aligned halves — the pro-rata recalc (what each component should have
 * been booked) and the USL actual (what Lusca actually booked) — separated by
 * a divider. The per-metric match verdict is folded onto the USL half: a green
 * value + tick when it reconciles with the recalc, red + cross when it does
 * not, so no separate verdict table is needed.
 *
 * This Cloudscape version has no spanning column header, so the two halves are
 * laid out with flex inside one column rather than as true sub-columns.
 */
const BundleAllocationTables = ({
  rows,
  paymentIntegrityPassed,
}: {
  rows: { [key: string]: { [key: string]: string } };
  paymentIntegrityPassed?: boolean;
}) => {
  const items: BundleAllocationRow[] = Object.entries(rows)
    .filter(([component]) => component !== BUNDLE_ALLOCATION_TOTAL_LABEL)
    .sort(([left], [right]) => left.localeCompare(right))
    .map(([component, cells]) => ({ component, cells, isTotal: false }));

  const totals = rows[BUNDLE_ALLOCATION_TOTAL_LABEL];
  if (totals) {
    items.push({
      component: BUNDLE_ALLOCATION_TOTAL_LABEL,
      cells: totals,
      isTotal: true,
    });
  }
  if (items.length === 0) return null;

  const metrics = BUNDLE_ALLOCATION_METRICS;

  const value = (row: BundleAllocationRow, cellKey: string) =>
    row.cells[cellKey] ?? "n/a";

  const componentColumn = (header: string) => ({
    id: "component",
    header,
    cell: (e: BundleAllocationRow) => (
      <Box fontWeight={e.isTotal ? "bold" : "normal"}>{e.component}</Box>
    ),
    minWidth: 280,
  });

  const sspColumn = {
    id: "ssp",
    header: "SSP",
    cell: (e: BundleAllocationRow) => (
      <Box textAlign="right" fontWeight={e.isTotal ? "bold" : "normal"}>
        {value(e, "ssp")}
      </Box>
    ),
  };

  const allocationPercentColumn = {
    id: "allocationPercent",
    header: "Allocation %",
    cell: (e: BundleAllocationRow) => (
      <Box textAlign="right" fontWeight={e.isTotal ? "bold" : "normal"}>
        {e.isTotal
          ? "100.00%"
          : e.cells.allocation_percent !== undefined
          ? `${e.cells.allocation_percent}%`
          : "n/a"}
      </Box>
    ),
  };

  // Each metric column splits into a Recalc half and a USL half, separated by a
  // divider. Header and cells share the same flex structure so they stay aligned.
  const splitRow: React.CSSProperties = { display: "flex" };
  const leftHalf: React.CSSProperties = {
    flex: 1,
    textAlign: "center",
    paddingRight: "8px",
    borderRight: "1px solid var(--color-border-divider-default, #d1d5db)",
  };
  const rightHalf: React.CSSProperties = {
    flex: 1,
    textAlign: "center",
    paddingLeft: "8px",
  };
  const metricColumns = metrics.map((metric) => ({
    id: metric.key,
    header: (
      <div style={{ textAlign: "center" }}>
        <div>{metric.label}</div>
        <div style={splitRow}>
          <span style={leftHalf}>
            <Box variant="span" fontSize="body-s" color="text-body-secondary">
              Recalc
            </Box>
          </span>
          <span style={rightHalf}>
            <Box variant="span" fontSize="body-s" color="text-body-secondary">
              USL
            </Box>
          </span>
        </div>
      </div>
    ),
    cell: (e: BundleAllocationRow) => {
      const proRata = e.cells[`pro_rata_${metric.key}`];
      const uslValue = e.cells[`usl_${metric.key}`];
      const bold = e.isTotal ? "bold" : "normal";
      const match = e.cells[`match_${metric.key}`];
      const passed = match === "true";
      // Colour + tick/cross only when there is both a booked value and a verdict.
      const uslColor =
        uslValue === undefined || match === undefined
          ? undefined
          : passed
          ? "text-status-success"
          : "text-status-error";
      return (
        <div style={splitRow}>
          <span style={leftHalf}>
            <Box variant="span" fontWeight={bold} color="text-body-secondary">
              {proRata ?? "n/a"}
            </Box>
          </span>
          <span style={rightHalf}>
            <Box variant="span" fontWeight={bold} color={uslColor}>
              {uslValue === undefined ? (
                "n/a"
              ) : uslColor === undefined ? (
                uslValue
              ) : (
                <>
                  <Icon
                    name={passed ? "status-positive" : "status-negative"}
                    variant={passed ? "success" : "error"}
                  />
                  {/* Screen-reader verdict so the match isn't conveyed by colour alone. */}
                  <span style={SR_ONLY}>
                    {passed ? "matched:" : "mismatch:"}
                  </span>{" "}
                  {uslValue}
                </>
              )}
            </Box>
          </span>
        </div>
      );
    },
  }));

  // Payment integrity compares the order payment total against the summed
  // component SSPs. Both numbers live on the Total row (summed SSP is its "ssp"
  // cell; payment_total rides the same cell map when the backend provides it),
  // so the check is shown with its inputs rather than as a bare verdict.
  const summedSsp = totals?.ssp;
  const paymentTotal = totals?.payment_total;
  const paymentIntegrityLabel = () => {
    const passed = paymentIntegrityPassed;
    const verdict = passed
      ? "Payment total matches summed SSPs"
      : "Payment total does not match summed SSPs";
    if (paymentTotal !== undefined && summedSsp !== undefined) {
      return `${verdict} (payment ${paymentTotal} vs summed SSPs ${summedSsp})`;
    }
    if (summedSsp !== undefined) {
      return `${verdict} (summed SSPs ${summedSsp})`;
    }
    return verdict;
  };

  return (
    <SpaceBetween size="xs">
      <Box variant="h4">Bundle Allocation</Box>
      {paymentIntegrityPassed !== undefined && (
        <StatusIndicator type={paymentIntegrityPassed ? "success" : "error"}>
          {paymentIntegrityLabel()}
        </StatusIndicator>
      )}
      <Box variant="small" color="text-body-secondary">
        Each metric splits into the pro-rata recalc and the USL actual; the USL
        value is marked ✓ when it reconciles, ✗ when it does not.
      </Box>
      <div style={{ fontSize: "12px", overflowX: "auto" }}>
        <Table
          items={items}
          columnDefinitions={[
            componentColumn("Bundle Content"),
            sspColumn,
            allocationPercentColumn,
            ...metricColumns,
          ]}
          variant="embedded"
        />
      </div>
    </SpaceBetween>
  );
};

const FinancialAmountsTable = ({
  rows,
}: {
  rows: { [key: string]: FinancialAmountRow };
}) => (
  <div style={{ fontSize: "12px", overflowX: "auto", width: "100%" }}>
    <Table
      items={Object.entries(rows).map(([key, row]) => ({ key, ...row }))}
      columnDefinitions={[
        {
          id: "element",
          header: "Element",
          cell: (e) => formatLabel(e.key),
          minWidth: 220,
        },
        {
          id: "source",
          header: "Source",
          cell: (e) => e.source || "-",
          minWidth: 140,
        },
        {
          id: "sourceAmount",
          header: "Source Amount",
          cell: (e) => e.sourceAmount || "-",
          minWidth: 140,
        },
        {
          id: "sourceCurr",
          header: "Source Curr",
          cell: (e) => e.sourceCurr || "-",
          minWidth: 120,
        },
        {
          id: "gammaUslAmount",
          header: "Gamma USL Amount",
          cell: (e) => {
            const mismatch = amountMismatch(
              e.sourceAmount,
              e.sourceCurr,
              e.gammaUslAmount,
              e.gammaUslCurr
            );
            return (
              <Box color={mismatch ? "text-status-error" : undefined}>
                {e.gammaUslAmount || "-"}
              </Box>
            );
          },
          minWidth: 160,
        },
        {
          id: "gammaUslCurr",
          header: "Gamma USL Curr",
          cell: (e) => <Box>{e.gammaUslCurr || "-"}</Box>,
          minWidth: 140,
        },
        {
          id: "prodUslAmount",
          header: "Prod USL Amount",
          cell: (e) => {
            const mismatch = amountMismatch(
              e.sourceAmount,
              e.sourceCurr,
              e.prodUslAmount,
              e.prodUslCurr
            );
            return (
              <Box color={mismatch ? "text-status-error" : undefined}>
                {e.prodUslAmount || "-"}
              </Box>
            );
          },
          minWidth: 160,
        },
        {
          id: "prodUslCurr",
          header: "Prod USL Curr",
          cell: (e) => <Box>{e.prodUslCurr || "-"}</Box>,
          minWidth: 140,
        },
      ]}
      variant="embedded"
    />
  </div>
);

interface PivotRow {
  id: string;
  glDesc: string;
  cells: { [key: string]: number };
  rowTotal: number;
  isTotal: boolean;
  children: PivotRow[];
}

const PivotTable = ({
  pivot,
  pivotByProduct,
}: {
  pivot: { [key: string]: { [key: string]: number } };
  pivotByProduct?: ShadowTestPivotByProduct;
}) => {
  const [expandedItems, setExpandedItems] = useState<PivotRow[]>([]);

  const accounts = useMemo(
    () =>
      Array.from(
        new Set(Object.values(pivot).flatMap((row) => Object.keys(row)))
      ).sort(),
    [pivot]
  );

  const items = useMemo<PivotRow[]>(() => {
    const rows = Object.entries(pivot).sort(([a], [b]) => a.localeCompare(b));
    const total = (cells: { [key: string]: number }) =>
      accounts.reduce((sum, account) => sum + (cells[account] ?? 0), 0);

    const colTotals: Record<string, number> = {};
    accounts.forEach((account) => (colTotals[account] = 0));
    rows.forEach(([, cells]) =>
      accounts.forEach(
        (account) =>
          (colTotals[account] =
            (colTotals[account] ?? 0) + (cells[account] ?? 0))
      )
    );

    const componentRows = (glDesc: string): PivotRow[] =>
      Object.entries(pivotByProduct?.[glDesc] ?? {})
        .sort(([left], [right]) => left.localeCompare(right))
        .map(([gti, cells]) => ({
          id: `${glDesc}-${gti}`,
          glDesc: gti,
          cells,
          rowTotal: total(cells),
          isTotal: false,
          children: [],
        }));

    return [
      ...rows.map(([glDesc, cells]) => ({
        id: glDesc,
        glDesc,
        cells,
        rowTotal: total(cells),
        isTotal: false,
        children: componentRows(glDesc),
      })),
      {
        id: "grand-total",
        glDesc: "Grand Total",
        cells: colTotals,
        rowTotal: accounts.reduce(
          (sum, account) => sum + colTotals[account],
          0
        ),
        isTotal: true,
        children: [],
      },
    ];
  }, [pivot, pivotByProduct, accounts]);

  const hasComponents = items.some((item) => item.children.length > 0);

  const fmt = (n: number) => (n === 0 ? "0" : n.toFixed(2));

  return (
    <div style={{ fontSize: "12px", overflowX: "auto" }}>
      <Table
        items={items}
        trackBy="id"
        columnDefinitions={[
          {
            id: "glDesc",
            header: "GL Line Description",
            cell: (e) => (
              <Box fontWeight={e.isTotal ? "bold" : "normal"}>{e.glDesc}</Box>
            ),
            minWidth: 280,
          },
          ...accounts.map((a) => ({
            id: a,
            header: a,
            cell: (e: PivotRow) => (
              <Box textAlign="right" fontWeight={e.isTotal ? "bold" : "normal"}>
                {fmt(e.cells[a] ?? 0)}
              </Box>
            ),
          })),
          {
            id: "grandTotal",
            header: "Grand Total",
            // Red flags an unbalanced total only on the Grand Total row, where a
            // nonzero sum means debits and credits don't net out. Per-GL-line and
            // per-component (GTI) rows legitimately have nonzero totals, so they
            // are never coloured.
            cell: (e) => (
              <Box
                textAlign="right"
                fontWeight={e.isTotal ? "bold" : "normal"}
                color={
                  e.isTotal && e.rowTotal !== 0
                    ? "text-status-error"
                    : undefined
                }
              >
                {fmt(e.rowTotal)}
              </Box>
            ),
          },
        ]}
        variant="embedded"
        expandableRows={
          hasComponents
            ? {
                getItemChildren: (item) => item.children,
                isItemExpandable: (item) => item.children.length > 0,
                expandedItems,
                onExpandableItemToggle: ({ detail }) =>
                  setExpandedItems((currentItems) =>
                    detail.expanded
                      ? [...currentItems, detail.item]
                      : currentItems.filter(
                          (item) => item.id !== detail.item.id
                        )
                  ),
                getItemsCount: (item) => item.children.length,
              }
            : undefined
        }
        ariaLabels={
          hasComponents
            ? {
                expandButtonLabel: (item) => `Expand ${item.glDesc}`,
                collapseButtonLabel: (item) => `Collapse ${item.glDesc}`,
              }
            : undefined
        }
      />
    </div>
  );
};

const CliCommandSection = ({ cliCommand }: { cliCommand: string }) => (
  <Box>
    <Box variant="h4">Run locally</Box>
    <CodeView
      content={cliCommand}
      actions={
        <CopyToClipboard
          copyButtonAriaLabel="Copy command"
          copyErrorText="Failed to copy"
          copySuccessText="Copied"
          textToCopy={cliCommand}
        />
      }
    />
  </Box>
);
